import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { setStoredToken } from "./api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      // Clear any stale token so subsequent requests don't pin a dead JWT
      // into the Authorization header.
      setStoredToken("");
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip /me check.
    // AuthCallback will exchange session_id and establish session first.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    // iter30 — handle verification-nudge magic-link tokens. The CTA URL
    // is `/dashboard?token=<jwt>`. Consume the token here BEFORE
    // ProtectedRoute can redirect to /login. On success, the access
    // cookie + Bearer fallback are set and `user` is populated, so
    // ProtectedRoute sees an authed session by the time Dashboard
    // mounts. The token query param is stripped before the route
    // component renders, so a back-navigation or reload doesn't try to
    // re-consume an already-used jti.
    if (typeof window !== "undefined") {
      const params = new URLSearchParams(window.location.search);
      const magicToken = params.get("token");
      if (magicToken && window.location.pathname === "/dashboard") {
        // Strip token from URL synchronously so a back-button or reload
        // doesn't try to re-consume an already-used jti.
        const cleanUrl = window.location.pathname + (window.location.hash || "");
        window.history.replaceState({}, "", cleanUrl);
        (async () => {
          let success = false;
          try {
            const { data } = await api.post("/auth/magic-login", { token: magicToken });
            if (data?.token) setStoredToken(data.token);
            if (data?.user) { setUser(data.user); success = true; }
          } catch (_e) {
            // Fall through to normal auth check / /login redirect.
          } finally {
            setLoading(false);
            if (success) {
              // Navigate to /profile (the nudge's intended destination)
              // via a full assign so React Router picks up the new path
              // with the fresh authed session.
              window.location.replace("/profile");
            }
          }
        })();
        return;
      }
    }
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    // Persist the JWT so iOS Safari (and any browser that strips the
    // `access_token` cookie under ITP / cookie partitioning) keeps a
    // working Authorization: Bearer fallback. The backend's
    // get_current_user already honors both paths.
    if (data?.token) setStoredToken(data.token);
    setUser(data.user);
    return data.user;
  };
  const register = async (email, password, name) => {
    const { data } = await api.post("/auth/register", { email, password, name });
    if (data?.token) setStoredToken(data.token);
    setUser(data.user);
    return data.user;
  };
  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    setStoredToken("");
    setUser(null);
  };
  const refreshUser = checkAuth;

  return (
    <AuthCtx.Provider value={{ user, loading, login, register, logout, refreshUser, setUser }}>
      {children}
    </AuthCtx.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error("useAuth outside provider");
  return ctx;
}
